import * as React from 'react'
import { Flex, Heading, Badge, colors, Card, settings, Paragraph, useTheme, StyledText } from '@dooer/react-ui-kit'
import { FormattedMessage } from 'react-intl'
import { AcademyList } from './academy-article-list'
import { useQuery } from 'react-apollo'
import { academyArticleSearchQuery } from './academy-article-search-query'
import notEmpty from '@dooer/not-empty/es'
import Spinner from '../../components/spinner'
import { useLocalStorageState } from '../../lib/utils'
import { IAcademyArticle } from './index'
import {
  DooerAcademyArticleSearch,
  DooerAcademyArticleSearchVariables,
} from './__generated__/DooerAcademyArticleSearch'
import { CardBody } from '../../components/card'
import { ReactComponent as IconSearch } from './assets/icon-search.svg'
import { AcademyCustomerCategory } from '../../__generated__/globalTypes'

interface ISearchResultViewProps {
  academySearch: string
  search: string | null
  location: {
    pathname: string
  }
  articlesWithPath: IAcademyArticle[]
}

const SearchResultView = (props: ISearchResultViewProps) => {
  const theme = useTheme()
  const [customerCategory] = useLocalStorageState<AcademyCustomerCategory | null>(
    '@@frontend-website/academyCustomerCategory',
    null
  )
  const { data, loading } = useQuery<DooerAcademyArticleSearch, DooerAcademyArticleSearchVariables>(
    academyArticleSearchQuery,
    {
      variables: {
        q: props.academySearch,
        filter: [
          'targets any in ["public"]',
          customerCategory && `customerCategories any eq ${JSON.stringify(customerCategory)}`,
        ]
          .filter(Boolean)
          .join(' and '),
      },
      skip: props.academySearch.trim() === '',
    }
  )

  const articleSearchArticles = (
    (data && data.academy && data.academy.articleSearch && data.academy.articleSearch.edges) ||
    []
  )
    .map((edge) => edge.node)
    .filter(notEmpty)

  return (
    <>
      {loading ? (
        <Flex justifyContent="center" row>
          <Spinner />
        </Flex>
      ) : (
        <>
          <Flex row>
            <Flex marginBottom="xxLarge">
              <div>
                <Heading flush level="h2" style={{ fontSize: '32px' }}>
                  <FormattedMessage
                    defaultMessage="Sökresultat: <i>{academySearch}</i>"
                    id="academy.search-result.heading"
                    values={{
                      i: (...parts: React.ReactNode[]) => <i>{parts}</i>,
                      academySearch: props.academySearch,
                    }}
                  />
                </Heading>
              </div>
            </Flex>
            <Flex marginLeft="small">
              <Badge style={{ background: colors.accents.brand, color: colors.text.light, border: 'none' }}>
                <FormattedMessage
                  defaultMessage="{totalCount} artiklar"
                  id="academy.search-result.total-count"
                  values={{ totalCount: articleSearchArticles.length }}
                />
              </Badge>
            </Flex>
          </Flex>
          {articleSearchArticles.length === 0 && (
            <Card style={{ boxShadow: settings.shadows.elevation[2] }}>
              <CardBody>
                <i>
                  <IconSearch />
                </i>
                <StyledText
                  component="h1"
                  size="huge"
                  style={{ color: theme.colors.accents.brand, marginBottom: theme.settings.spacing.medium }}
                  weight="300"
                >
                  <FormattedMessage defaultMessage="Inga resultat hittades" id="academy.search.no-result.header" />
                </StyledText>
                <Paragraph bold textAlign="center" variant="dim">
                  <FormattedMessage
                    defaultMessage="Testa att söka igen på ett annat nyckelord. Hittar du inte informationen som du söker efter, kan du alltid kontakta oss och fråga."
                    id="academy.search.no-result.sub-header"
                  />
                </Paragraph>
              </CardBody>
            </Card>
          )}
          <AcademyList
            academySearch={props.academySearch}
            articles={props.articlesWithPath.filter((a) => articleSearchArticles.some((as) => as.id === a.id))}
            isRoot={false}
            isSearch
          />
        </>
      )}
    </>
  )
}

export default SearchResultView
